"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"; // 既存のものを再利用
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * テキストが省略（トランケート）されている場合のみツールチップを表示するコンポーネント。
 *
 * - TooltipTrigger の子要素が省略表示されている場合のみ TooltipContent を表示します。
 * - モバイルでは長押しでツールチップを表示します。
 * - デスクトップでは通常のホバーで表示します。
 *
 * @param children TooltipTrigger と TooltipContent を含むReactノード
 * @param その他 TooltipPrimitive.Root のprops
 */
interface TruncateTooltipProps
  extends React.ComponentProps<typeof TooltipPrimitive.Root> {
  /**
   * TooltipTrigger と TooltipContent を含むReactノード
   */
  children: React.ReactNode;
}

/**
 * TruncateTooltip
 *
 * テキストが省略（トランケート）されている場合のみツールチップを表示するラッパーコンポーネント。
 *
 * - TooltipTrigger の子要素が省略表示されている場合のみ TooltipContent を表示します。
 * - モバイルでは長押しでツールチップを表示します。
 * - デスクトップでは通常のホバーで表示します。
 *
 * @example
 * <TruncateTooltip>
 *   <TooltipTrigger>
 *     <span style={{ width: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 *       省略されるかもしれない長いテキスト
 *     </span>
 *   </TooltipTrigger>
 *   <TooltipContent>
 *     省略されるかもしれない長いテキスト
 *   </TooltipContent>
 * </TruncateTooltip>
 */
const TruncateTooltipRoot = ({ children, ...props }: TruncateTooltipProps) => {
  const [isTruncated, setIsTruncated] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerInnerRef = React.useRef<HTMLElement | null>(null);
  const isMobile = useIsMobile(); // 追加
  const timerRef = React.useRef<NodeJS.Timeout | null>(null); // 追加
  const longPressTimerDidFire = React.useRef(false); // 追加: タイマーが発火したか
  const [ignoreClickAfterLongPress, setIgnoreClickAfterLongPress] =
    React.useState(false); // 追加

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (isTruncated) {
        setIsOpen(open);
      } else {
        setIsOpen(false); // 省略されていなければ常に閉じる
      }
    },
    [isTruncated], // isTruncated に依存
  );

  // タッチイベントハンドラを追加
  const handleTouchStart = React.useCallback(() => {
    if (!isMobile || !isTruncated) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (isOpen) return;
    longPressTimerDidFire.current = false; // リセット

    timerRef.current = setTimeout(() => {
      // isTruncated のチェックは handleOpenChange 内で行われるので、ここでは直接 setIsOpen(true) でも良いが、
      // handleOpenChange を経由することでロジックを一元化する
      handleOpenChange(true);
      longPressTimerDidFire.current = true; // タイマーが発火したことを記録
    }, 300);
  }, [isMobile, isTruncated, isOpen, handleOpenChange]); // handleOpenChange を依存配列に追加

  const handleTouchEnd = React.useCallback(() => {
    if (!isMobile) return;

    if (timerRef.current) {
      // 長押し成立前に指を離した
      clearTimeout(timerRef.current);
      timerRef.current = null;
      // longPressTimerDidFire.current は false のまま
    }
    // timerRef.current が null の場合は、タイマーが発火したか、上記でクリアされたか。

    if (isOpen) {
      handleOpenChange(false); // Tooltipが開いていたら閉じる
    }

    if (longPressTimerDidFire.current) {
      // タイマーが一度でも発火していたらクリック抑制
      setIgnoreClickAfterLongPress(true);
      setTimeout(() => {
        setIgnoreClickAfterLongPress(false);
      }, 200); // 有効期間を200msに延長
      longPressTimerDidFire.current = false; // リセット
    }
  }, [isMobile, isOpen, handleOpenChange]); // handleOpenChange を依存配列に追加

  React.useEffect(() => {
    // クリーンアップ用
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const triggerElementWithRef = React.useMemo(() => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child) && child.type === TooltipTrigger) {
        const triggerNode = child as React.ReactElement<
          React.PropsWithChildren<React.ComponentProps<typeof TooltipTrigger>>
        >;
        const originalTriggerChildren = triggerNode.props.children;

        if (
          React.Children.count(originalTriggerChildren) === 1 &&
          React.isValidElement(originalTriggerChildren)
        ) {
          const touchProps = isMobile
            ? {
                onTouchStart: handleTouchStart,
                onTouchEnd: handleTouchEnd,
                onContextMenu: (e: React.MouseEvent) => {
                  if (isOpen) {
                    e.preventDefault();
                  }
                },
                onClickCapture: (e: React.MouseEvent) => {
                  if (ignoreClickAfterLongPress) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                },
              }
            : {};

          const childWithRefAndTouch = React.cloneElement(
            originalTriggerChildren as React.ReactElement<{
              ref?: React.Ref<HTMLElement>;
            }>,
            {
              ref: triggerInnerRef,
              ...touchProps,
            },
          );
          return React.cloneElement(triggerNode, {}, childWithRefAndTouch);
        }
        console.warn(
          "TruncateTooltip: TooltipTrigger's child is not a single React element or does not accept ref. Truncation check/touch events might not work as expected.",
        );
        return child;
      }
      return child;
    });
  }, [
    children,
    isMobile,
    handleTouchStart,
    handleTouchEnd,
    isOpen,
    ignoreClickAfterLongPress,
  ]); // ignoreClickAfterLongPress を依存配列に追加

  const checkTruncation = React.useCallback(() => {
    const element = triggerInnerRef.current;
    if (element) {
      const isCurrentlyTruncated =
        element.scrollWidth > element.clientWidth ||
        element.scrollHeight > element.clientHeight;
      // setIsTruncated の呼び出しは isTruncated の現在の値と比較してから行う
      // この比較は既にsetIsTruncatedの呼び出し側で行われているため、ここでは不要
      // ただし、isTruncated の値を直接参照すると stale closure の問題が発生する可能性があるため、
      // setIsTruncated(prev => newState) の形式を使うか、
      // isTruncated を useCallback の依存配列に入れる必要がある。
      // 今回は、setIsTruncated の呼び出し自体をデバウンスするため、
      // デバウンスされた関数内で最新の isTruncated を参照する形は取らない。
      // isCurrentlyTruncated の値を setIsTruncated に渡すだけでよい。
      setIsTruncated(isCurrentlyTruncated);
    }
  }, []); // isTruncated を依存配列から削除し、setIsTruncated(value) の形式で更新

  // biome-ignore lint/correctness/useExhaustiveDependencies: 許容
  React.useLayoutEffect(() => {
    // デバウンスされたチェック関数
    let debounceTimeoutId: NodeJS.Timeout;
    const debouncedCheckTruncation = () => {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = setTimeout(() => {
        checkTruncation();
      }, 150); // 150msのデバウンスタイム
    };

    debouncedCheckTruncation(); // 初期チェック (デバウンス付き)

    const targetNode = triggerInnerRef.current;
    if (!targetNode) return;

    const resizeObserver = new ResizeObserver(debouncedCheckTruncation);
    resizeObserver.observe(targetNode);

    const mutationObserver = new MutationObserver(debouncedCheckTruncation);
    mutationObserver.observe(targetNode, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => {
      clearTimeout(debounceTimeoutId); // クリーンアップ時にタイムアウトをクリア
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [triggerElementWithRef, checkTruncation]); // isTruncated を削除し、checkTruncation を追加

  // children の中に TooltipTrigger が見つからなかった場合のフォールバック
  // または、TooltipTrigger が期待通りにラップできなかった場合
  // この場合は通常のTooltipとして動作させるか、エラーを出すか検討
  // ここでは、childrenをそのままレンダリングし、TruncateTooltipの機能は無効とする

  return (
    <TooltipPrimitive.Root
      {...props}
      open={isTruncated ? isOpen : false}
      onOpenChange={handleOpenChange}
      disableHoverableContent={true}
    >
      {triggerElementWithRef}
    </TooltipPrimitive.Root>
  );
};

// TruncateTooltip専用のTooltipContent
const TruncateTooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipContent>
>(({ className, ...props }, ref) => {
  return (
    <TooltipContent
      ref={ref}
      className={className}
      style={{ pointerEvents: "none" }}
      {...props}
    />
  );
});
TruncateTooltipContent.displayName = "TruncateTooltipContent";

export {
  TooltipTrigger, // from "@/components/ui/tooltip"
  TruncateTooltipContent as TooltipContent, // TruncateTooltip専用のTooltipContent
  TruncateTooltipRoot as TruncateTooltip,
};
